import React, { useState, useEffect, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import MeasurementPrivacy from './pages/MeasurementPrivacy';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import NotFound from './pages/NotFound';
import Apartments from './pages/Apartments';
import ApartmentDetail from './pages/ApartmentDetail';
import AdminDashboard from './pages/AdminDashboard';
import AdminUsers from './pages/AdminUsers';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminUbyportPage from './pages/AdminUbyportPage';
import AdminEmail from './pages/AdminEmail';
import AdminCityTaxMailboxes from './pages/AdminCityTaxMailboxes';
import AdminScheduledJobs from './pages/AdminScheduledJobs';
import AdminApiUsage from './pages/AdminApiUsage';
import AdminBilling from './pages/AdminBilling';
import AdminBillingDetail from './pages/AdminBillingDetail';
import AdminBillingSettings from './pages/AdminBillingSettings';
import './App.css';
import Reservations from './pages/Reservations';
import ReservationDetail from './pages/ReservationDetail';
import ReservationCheckinForm from './pages/ReservationCheckinForm';
import CityTaxReportPage from './pages/CityTaxReport';
import CleaningCalendarPage from './pages/CleaningCalendar';
import CheckinGuidePage from './pages/CheckinGuidePage';
import PublicStripeInvite from './pages/PublicStripeInvite';
import PublicStayExtensionPay from './pages/PublicStayExtensionPay';
import StayExtensionRequests from './pages/StayExtensionRequests';
import { getPendingStayExtensionCount } from './api.stayExtension';
import PublicCheckinStart from './pages/PublicCheckinStart';
import PublicCheckinForm from './pages/PublicCheckinForm';
import ManagerProfile from './pages/ManagerProfile';
import Subscription from './pages/Subscription';
import PricingCalendar from './pages/PricingCalendar';
import LockDetail from './pages/LockDetail';
import { AUTH_STATE_CHANGED_EVENT, clearStoredAuthState, getStoredAuthState, isAuthenticated } from './auth';
import { getMyProfile } from './api.managerProfile';
import { setupAuthInterceptor } from './api';
import ErrorBoundary from './components/ErrorBoundary';
import HomeIcon from './components/icons/HomeIcon';
import DashboardIcon from './components/icons/DashboardIcon';
import LoginIcon from './components/icons/LoginIcon';
import RegisterIcon from './components/icons/RegisterIcon';
import ApartmentsIcon from './components/icons/ApartmentsIcon';
import ManagersIcon from './components/icons/ManagersIcon';
import AdminIcon from './components/icons/AdminIcon';
import LogoutIcon from './components/icons/LogoutIcon';
import PersonIcon from './components/icons/PersonIcon';
import SettingsIcon from './components/icons/SettingsIcon';
import BookIcon from './components/icons/BookIcon';
import CalendarIcon from './components/icons/CalendarIcon';
import { useSetupStep } from './hooks/useSetupStep';
import { useLang } from './i18n/LanguageContext';
import { common } from './i18n/pages/common';

setupAuthInterceptor();

const NavIcon: React.FC<{ name: 'home' | 'dashboard' | 'login' | 'register' | 'apartments' | 'reservations' | 'managers' | 'admin' | 'logout' | 'profile' | 'settings' | 'calendar' }> = ({ name }) => {
  switch (name) {
    case 'home': return <HomeIcon />;
    case 'dashboard': return <DashboardIcon />;
    case 'login': return <LoginIcon />;
    case 'register': return <RegisterIcon />;
    case 'apartments': return <ApartmentsIcon />;
    case 'reservations': return <BookIcon size={18} />;
    case 'managers': return <ManagersIcon />;
    case 'admin': return <AdminIcon />;
    case 'logout': return <LogoutIcon />;
    case 'profile': return <PersonIcon />;
    case 'settings': return <SettingsIcon />;
    case 'calendar': return <CalendarIcon size={18} />;
    default: return null;
  }
};

const Navigation: React.FC = () => {
  const { lang } = useLang();
  const tc = common[lang];
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [pendingExtensions, setPendingExtensions] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const setupStep = useSetupStep(userEmail, userRole, location.pathname);
  const cueText = setupStep === 'ADD_APARTMENT' ? tc.navCueAddApartment
    : setupStep === 'CONNECT_CALENDAR' ? tc.navCueConnectCalendar : null;

  const checkAuth = () => {
    const { userEmail, userRole } = getStoredAuthState();
    setUserEmail(userEmail);
    setUserRole(userRole);
  };

  useEffect(() => {
    checkAuth();
  }, [location]);

  useEffect(() => {
    window.addEventListener('storage', checkAuth);
    window.addEventListener(AUTH_STATE_CHANGED_EVENT, checkAuth);
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener(AUTH_STATE_CHANGED_EVENT, checkAuth);
    };
  }, []);

  // Re-read on every navigation rather than polling: a manager who has just answered one should
  // see the badge drop, and any click already costs a render.
  useEffect(() => {
    if (!userEmail || userRole === 'ADMIN') {
      setPendingExtensions(0);
      return;
    }
    getPendingStayExtensionCount()
      .then(setPendingExtensions)
      .catch(() => setPendingExtensions(0));
  }, [userEmail, userRole, location.pathname]);

  const handleLogout = () => {
    clearStoredAuthState();
    setUserEmail(null);
    setUserRole(null);
    setPendingExtensions(0);
    navigate('/login');
  };

  const linkClass = (path: string) => {
    const isActive = location.pathname === path;
    return `top-nav-link${isActive ? ' active' : ''}`;
  };

  return (
    <nav className="top-nav">
      {/* Brand logo — hidden on mobile (bottom nav has no room for it) */}
      <Link to="/" className="nav-logo desktop-only" style={{ marginRight: 16 }}>
        <div className="nav-logo-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
        </div>
        <span className="nav-logo-text">Guestbook<span>.cz</span></span>
      </Link>

      {/* Links — on desktop: horizontal top bar; on mobile: fixed bottom bar */}
      <div className="top-nav-links">
        {userRole !== 'ADMIN' && (
          <Link to="/" className={linkClass('/')}>
            {/* Anything waiting on this manager is counted here rather than earning a tab of its
                own. On mobile the nav is a fixed bottom bar of equal-width items, so a conditional
                tab reflows every other column the moment a request arrives — the button someone
                was reaching for moves under their thumb. A badge costs no width and moves nothing. */}
            {/* Positioned from App.css, not inline: the desktop bar lays the icon out beside its
                label and the mobile bar stacks them, so one set of offsets cannot be right for
                both. Anchored to the link rather than the icon for the same reason. */}
            <span className="nav-icon"><NavIcon name="dashboard" /></span>
            <span className="nav-label">{tc.navDashboard}</span>
            {pendingExtensions > 0 && (
              <span className="nav-badge" aria-label={`${pendingExtensions}`}>
                {pendingExtensions > 9 ? '9+' : pendingExtensions}
              </span>
            )}
          </Link>
        )}
        {!userEmail && (
          <Link to="/login" className={linkClass('/login')}>
            <span className="nav-icon"><NavIcon name="login" /></span>
            <span className="nav-label">{tc.navLogin}</span>
          </Link>
        )}
        {!userEmail && (
          <Link to="/register" className={linkClass('/register')}>
            <span className="nav-icon"><NavIcon name="register" /></span>
            <span className="nav-label">{tc.navRegister}</span>
          </Link>
        )}
        {userEmail && userRole !== 'ADMIN' && (
          // The href never changes; the cue rides along as router state so the Apartments page
          // knows to spotlight the next button only for someone who followed the cue.
          <Link
            to="/apartments"
            state={cueText ? { setupCue: true } : undefined}
            className={linkClass('/apartments')}
            title={cueText ?? undefined}
          >
            <span className="nav-icon"><NavIcon name="apartments" /></span>
            <span className="nav-label">{tc.navApartments}</span>
            {cueText && (
              <>
                <span className="visually-hidden">, {cueText}</span>
                <span className="nav-cue-dot" aria-hidden="true" />
                <span className="nav-cue-tip" aria-hidden="true">{cueText}</span>
              </>
            )}
          </Link>
        )}
        {userEmail && userRole !== 'ADMIN' && (
          <Link to="/reservations" className={linkClass('/reservations')}>
            <span className="nav-icon"><NavIcon name="reservations" /></span>
            <span className="nav-label">{tc.navReservations}</span>
          </Link>
        )}

        {userEmail && userRole !== 'ADMIN' && (
          <Link to="/calendar" className={linkClass('/calendar')}>
            <span className="nav-icon"><NavIcon name="calendar" /></span>
            <span className="nav-label">{tc.navCalendar}</span>
          </Link>
        )}
        {userEmail && userRole !== 'ADMIN' && (
          <Link to="/profile" className={linkClass('/profile')}>
            <span className="nav-icon"><NavIcon name="settings" /></span>
            <span className="nav-label">{tc.navSettings}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin" className={linkClass('/admin')}>
            <span className="nav-icon"><NavIcon name="dashboard" /></span>
            <span className="nav-label">{tc.navDashboard}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/users" className={linkClass('/admin/users')}>
            <span className="nav-icon"><NavIcon name="managers" /></span>
            <span className="nav-label">{tc.navUsers}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/billing" className={linkClass('/admin/billing')}>
            <span className="nav-icon"><NavIcon name="reservations" /></span>
            <span className="nav-label">{tc.navBilling}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/ubyport" className={linkClass('/admin/ubyport')}>
            <span className="nav-icon"><NavIcon name="settings" /></span>
            <span className="nav-label">{tc.navUbyport}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/email" className={linkClass('/admin/email')}>
            <span className="nav-icon"><NavIcon name="admin" /></span>
            <span className="nav-label">{tc.navEmail}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/city-tax-mailboxes" className={linkClass('/admin/city-tax-mailboxes')}>
            <span className="nav-icon"><NavIcon name="settings" /></span>
            <span className="nav-label">{tc.navCityTaxMailboxes}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/scheduled-jobs" className={linkClass('/admin/scheduled-jobs')}>
            <span className="nav-icon"><NavIcon name="settings" /></span>
            <span className="nav-label">{tc.navScheduledJobs}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <Link to="/admin/api-usage" className={linkClass('/admin/api-usage')}>
            <span className="nav-icon"><NavIcon name="settings" /></span>
            <span className="nav-label">{tc.navApiUsage}</span>
          </Link>
        )}
        {userEmail && userRole === 'ADMIN' && (
          <button onClick={handleLogout} className="top-nav-mobile-logout">
            <span className="nav-icon"><NavIcon name="logout" /></span>
            <span className="nav-label">{tc.navLogout}</span>
          </button>
        )}
      </div>

      {/* Desktop-only: user email + logout button on the right */}
      {userEmail && (
        <div className="top-nav-user">
          <span className="top-nav-user-label">{userEmail}</span>
          <button onClick={handleLogout} className="btn btn-sm btn-danger" style={{ borderRadius: 8 }}>{tc.navLogout}</button>
        </div>
      )}
    </nav>
  );
};

const RootRoute: React.FC = () => {
  const { userRole } = getStoredAuthState();
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (userRole === 'ADMIN') {
    return <Navigate to="/admin" replace />;
  }
  return <Home />;
};

const UserOnlyRoute: React.FC<{ element: ReactNode }> = ({ element }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return <>{element}</>;
};

const AdminOnlyRoute: React.FC<{ element: ReactNode }> = ({ element }) => {
  const { userRole } = getStoredAuthState();
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (userRole !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }
  return <>{element}</>;
};


const SuspensionBanner: React.FC = () => {
  const [suspended, setSuspended] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();
  const { lang } = useLang();
  const tc = common[lang];
  const { userRole } = getStoredAuthState();

  useEffect(() => {
    if (!isAuthenticated() || userRole === 'ADMIN') return;
    getMyProfile(localStorage.getItem('token') || '')
      .then(p => setSuspended(p.billingStatus === 'SUSPENDED'))
      .catch(() => {});
  }, [location.pathname]);

  if (!suspended || dismissed || userRole === 'ADMIN') return null;

  return (
    <div style={{
      background: '#fff5f5', borderBottom: '1px solid #fed7d7',
      padding: '10px 20px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    }}>
      <span style={{ color: '#c53030', fontWeight: 600, fontSize: '0.9rem' }}>
        ⚠ {tc.suspendedMsg}{' '}
        <a href="/subscription" style={{ color: '#c53030', textDecoration: 'underline' }}>{tc.suspendedViewSubscription}</a>
      </span>
      <button
        onClick={() => setDismissed(true)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9b2c2c', fontSize: '1.1rem', padding: 0, lineHeight: 1 }}
        aria-label={tc.close}
      >✕</button>
    </div>
  );
};

const ImpersonationBanner: React.FC = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');
  const name = localStorage.getItem('impersonatingName');
  if (!adminToken) return null;

  const exit = () => {
    const returnPath = localStorage.getItem('adminReturnPath') || '/admin/users';
    localStorage.setItem('token', localStorage.getItem('adminToken') || '');
    localStorage.setItem('userEmail', localStorage.getItem('adminUserEmail') || '');
    localStorage.setItem('userRole', localStorage.getItem('adminUserRole') || '');
    localStorage.setItem('userId', localStorage.getItem('adminUserId') || '');
    ['adminToken', 'adminUserEmail', 'adminUserRole', 'adminUserId', 'adminReturnPath', 'impersonatingName'].forEach(k => localStorage.removeItem(k));
    window.dispatchEvent(new Event('guestbook-auth-state-changed'));
    navigate(returnPath);
  };

  return (
    <div style={{
      background: '#553c9a', color: '#fff',
      padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 10,
      fontSize: '0.85rem', fontWeight: 600, flexShrink: 0,
    }}>
      <span>👤 Přihlášen jako: <strong>{name}</strong> &nbsp;·&nbsp; token platí 1h</span>
      <button onClick={exit} style={{
        marginLeft: 'auto', padding: '4px 14px', borderRadius: 6,
        background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
        color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
      }}>
        ← Zpět do admin
      </button>
    </div>
  );
};

// Routes reached by people who have no Guestbook login: guests, cleaners, apartment owners. They
// get the page and nothing else — the nav is a manager's tool, and on mobile it is a fixed bar
// pinned to the bottom of the screen, so leaking it onto a guest page both offers them links they
// cannot use and eats the bottom of whatever they came to read.
const PUBLIC_NO_NAV_PREFIXES = [
  '/guide/', '/cleaning/', '/check-in/', '/stripe-invite/', '/stay-extension/',
];

const AppShell: React.FC = () => {
  const location = useLocation();
  const isPublic = PUBLIC_NO_NAV_PREFIXES.some(p => location.pathname.startsWith(p));

  return (
    <div className={isPublic ? undefined : 'app-shell'}>
      {!isPublic && <ImpersonationBanner />}
      {!isPublic && <Navigation />}
      {!isPublic && <SuspensionBanner />}
      <div className={isPublic ? undefined : 'app-content'}>
        <Routes>
          <Route path="/privacy/measurement" element={<MeasurementPrivacy />} />
          <Route path="/" element={<RootRoute />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/apartments" element={<UserOnlyRoute element={<Apartments />} />} />
          <Route path="/apartments/:id" element={<UserOnlyRoute element={<ApartmentDetail />} />} />
          <Route path="/apartments/:id/city-tax" element={<UserOnlyRoute element={<CityTaxReportPage />} />} />
          <Route path="/reservations" element={<UserOnlyRoute element={<Reservations />} />} />
          <Route path="/reservations/:id" element={<UserOnlyRoute element={<ReservationDetail />} />} />
          <Route path="/reservations/:id/checkin-form" element={<UserOnlyRoute element={<ReservationCheckinForm />} />} />
          <Route path="/profile" element={<UserOnlyRoute element={<ManagerProfile />} />} />
          <Route path="/subscription" element={<UserOnlyRoute element={<Subscription />} />} />
          <Route path="/calendar" element={<UserOnlyRoute element={<PricingCalendar />} />} />
          <Route path="/stay-extensions" element={<UserOnlyRoute element={<StayExtensionRequests />} />} />
          <Route path="/locks/:lockId" element={<UserOnlyRoute element={<LockDetail />} />} />

          {/* Public cleaning calendar (no auth) */}
          <Route path="/cleaning/:privateKey" element={<CleaningCalendarPage />} />

          {/* Public guest check-in flow */}
          <Route path="/check-in/:publicKey" element={<PublicCheckinStart />} />
          <Route path="/check-in/form/:formToken" element={<PublicCheckinForm />} />

          {/* Public check-in guide (no auth) */}
          <Route path="/guide/:formToken" element={<CheckinGuidePage />} />

          {/* Apartment owner connecting their own Stripe account (no auth — the invitation
              token is the authorisation, and the owner never gets a Guestbook login) */}
          <Route path="/stripe-invite/:token" element={<PublicStripeInvite />} />
          {/* Paying for approved extra hours. Its own token, deliberately not the guide's:
              forwarding a payment link must never hand over the door code with it. */}
          <Route path="/stay-extension/:paymentToken" element={<PublicStayExtensionPay />} />

          <Route path="/admin" element={<AdminOnlyRoute element={<AdminDashboard />} />} />
          <Route path="/admin/users" element={<AdminOnlyRoute element={<AdminUsers />} />} />
          <Route path="/admin/users/:id" element={<AdminOnlyRoute element={<AdminUserDetail />} />} />
          <Route path="/admin/billing" element={<AdminOnlyRoute element={<AdminBilling />} />} />
          <Route path="/admin/billing/settings" element={<AdminOnlyRoute element={<AdminBillingSettings />} />} />
          <Route path="/admin/billing/:managerId" element={<AdminOnlyRoute element={<AdminBillingDetail />} />} />
          <Route path="/admin/ubyport" element={<AdminOnlyRoute element={<AdminUbyportPage />} />} />
          <Route path="/admin/email" element={<AdminOnlyRoute element={<AdminEmail />} />} />
          <Route path="/admin/city-tax-mailboxes" element={<AdminOnlyRoute element={<AdminCityTaxMailboxes />} />} />
          <Route path="/admin/scheduled-jobs" element={<AdminOnlyRoute element={<AdminScheduledJobs />} />} />
          <Route path="/admin/api-usage" element={<AdminOnlyRoute element={<AdminApiUsage />} />} />
          <Route path="/admin/apartments/:id" element={<AdminOnlyRoute element={<ApartmentDetail />} />} />

          <Route path="/settings/payment-methods" element={<Navigate to="/profile" replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </div>
  );
};

const App: React.FC = () => (
  <ErrorBoundary>
    <Router>
      <AppShell />
    </Router>
  </ErrorBoundary>
);

export default App;
